import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import AudioRecorder from '../AudioRecorder';
import { BobineSceau } from '../BobineSceau';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

/* ── Isolated Caption Input ──
   Local state prevents parent re-render on every keystroke.
   Only syncs upward via debounced saveCaption.
*/
const IsolatedCaptionInput = memo(({ photoId, initialCaption, saveCaption, placeholder, inputFocusedRef }) => {
  const inputRef = useRef(null);

  // Sync only when navigating to a different photo
  const prevPhotoId = useRef(photoId);
  useEffect(() => {
    if (prevPhotoId.current !== photoId) {
      if (inputRef.current) inputRef.current.value = initialCaption || '';
      prevPhotoId.current = photoId;
    }
  }, [photoId, initialCaption]);

  const handleInput = useCallback((e) => {
    saveCaption(photoId, e.target.value);
  }, [photoId, saveCaption]);

  const handleFocus = useCallback(() => {
    if (inputFocusedRef) inputFocusedRef.current = true;
    // ARRÊT TOTAL : geler animations + transitions de toute l'app
    document.body.classList.add('bobine-keyboard-active');
  }, [inputFocusedRef]);

  const handleBlur = useCallback(() => {
    if (inputFocusedRef) inputFocusedRef.current = false;
    document.body.classList.remove('bobine-keyboard-active');
  }, [inputFocusedRef]);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      border: '1px solid rgba(212,175,55,0.25)', borderRadius: 8,
      padding: '8px 14px', marginBottom: 8,
      backgroundColor: 'rgba(212,175,55,0.03)',
    }}>
      <BobineSceau size={18} />
      <input
        ref={inputRef}
        data-testid="viewer-caption-input"
        type="text"
        defaultValue={initialCaption || ''}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        onClick={e => e.stopPropagation()}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: '100%', textAlign: 'left',
          fontFamily: "'Georgia', serif", fontSize: 16,
          color: '#D4AF37', fontStyle: 'italic',
          backgroundColor: 'transparent', border: 'none',
          padding: '2px 0', outline: 'none',
        }}
      />
    </div>
  );
});

const PhotoViewer = ({
  photos, viewerIndex, setViewerIndex, closeViewer,
  coffSwipe, rotation, handleRotate,
  restoring, restoreProgress, handleRestore,
  editingCaption, setEditingCaption, saveCaption,
  pendingComments, token, setPhotos, t,
}) => {
  const contentRef = useRef(null);
  const hdRef = useRef(null);
  const photo = photos[viewerIndex];
  const isMobile = window.innerWidth < 768;
  const inputFocusedRef = coffSwipe.inputFocused;

  // Preload ±3 thumbnails with decode() so swap is instant — SKIP when typing
  useEffect(() => {
    if (!photo) return;
    if (contentRef.current) contentRef.current.scrollTop = 0;
    if (inputFocusedRef?.current) return;
    const count = isMobile ? 3 : 6;
    for (let offset = -count; offset <= count; offset++) {
      if (offset === 0) continue;
      const p = photos[viewerIndex + offset];
      if (p) {
        const img = new Image();
        img.src = `${API}/photos/${p.id}/thumbnail?token=${token}`;
        if (img.decode) img.decode().catch(() => {});
      }
    }
  }, [viewerIndex, photos, token, photo, isMobile, inputFocusedRef]);

  // PC only: When photo changes, reset HD overlay
  useEffect(() => {
    if (!isMobile && hdRef.current) {
      hdRef.current.style.opacity = '0';
    }
  }, [viewerIndex, isMobile]);

  // Cleanup: remove keyboard freeze class when viewer unmounts
  useEffect(() => {
    return () => document.body.classList.remove('bobine-keyboard-active');
  }, []);

  if (!photo) return null;

  return (
    <div
      data-testid="photo-viewer"
      onMouseMove={coffSwipe.handleMouseMove}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        backgroundColor: '#000',
        display: 'flex', flexDirection: 'column',
        animation: 'fadeIn 0.3s ease-out',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Top toolbar — compact for mobile */}
      <div
        data-testid="viewer-toolbar"
        style={{
          position: 'relative', zIndex: 70,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px',
          backgroundColor: 'rgba(0,0,0,0.85)',
          borderBottom: '1px solid rgba(212,175,55,0.1)',
          flexShrink: 0,
        }}
      >
        {/* Close button — large tap target */}
        <button
          data-testid="viewer-close"
          onClick={(e) => { e.stopPropagation(); closeViewer(); }}
          style={{
            background: 'none', border: 'none',
            cursor: 'pointer', padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 6,
            color: '#D4AF37',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37' }}>{t('viewer_close') || 'Fermer'}</span>
        </button>

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Restore button */}
          <button
            data-testid="viewer-restore"
            onClick={e => { e.stopPropagation(); handleRestore(photo.id); }}
            disabled={restoring}
            style={{
              background: 'none', border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: 20, height: 36, padding: '0 12px',
              cursor: restoring ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37',
              position: 'relative', overflow: 'hidden',
            }}
          >
            {restoring && (
              <div style={{
                position: 'absolute', left: 0, bottom: 0, height: 3,
                width: `${restoreProgress}%`,
                background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                transition: 'width 0.4s ease-out', borderRadius: 2,
              }} />
            )}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3a9 9 0 0 0-9 9h3l-4 4-4-4h3a11 11 0 0 1 11-11z" />
              <path d="M12 21a9 9 0 0 0 9-9h-3l4-4 4 4h-3a11 11 0 0 1-11 11z" />
            </svg>
            <span className="hidden sm:inline">{restoring ? `${Math.round(restoreProgress)}%` : t('viewer_restore_btn')}</span>
          </button>

          {/* Rotate button */}
          <button
            data-testid="viewer-rotate"
            onClick={handleRotate}
            style={{
              background: 'none', border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: '50%', width: 36, height: 36,
              cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
        </div>
      </div>

      {/* Navigation arrows — use carousel goToNext/goToPrev */}
      {viewerIndex > 0 && (
        <button
          data-testid="viewer-prev"
          onClick={e => { e.stopPropagation(); coffSwipe.goToPrev(); }}
          style={{
            position: 'fixed', left: 4, top: '45%', transform: 'translateY(-50%)',
            zIndex: 68, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.3)',
            borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}
      {viewerIndex < photos.length - 1 && (
        <button
          data-testid="viewer-next"
          onClick={e => { e.stopPropagation(); coffSwipe.goToNext(); }}
          style={{
            position: 'fixed', right: 4, top: '45%', transform: 'translateY(-50%)',
            zIndex: 68, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.3)',
            borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      )}

      {/* Restore loader overlay */}
      {restoring && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', inset: 0, zIndex: 75,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }} data-testid="restore-loader">
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            border: '3px solid rgba(212,175,55,0.15)',
            borderTopColor: '#D4AF37',
            animation: 'spin 1.2s linear infinite',
            marginBottom: 20,
          }} />
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#D4AF37', fontStyle: 'italic' }}>
            {t('viewer_restore_progress')}
          </p>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', marginTop: 8 }}>
            {Math.round(restoreProgress)}%
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Scrollable content: Photo + Metadata */}
      {!restoring && (
        <div
          ref={contentRef}
          data-no-close
          onClick={e => e.stopPropagation()}
          style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}
        >
          {/* Photo — single container, thumbnail only on mobile, +HD on PC */}
          <div ref={coffSwipe.photoRef} data-testid="viewer-photo-container" style={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', height: '75vh', flexShrink: 0,
            touchAction: 'pan-y', overflow: 'hidden', backgroundColor: '#000',
          }}>
            <img
              src={`${API}/photos/${photo.id}/thumbnail?token=${token}`}
              alt={photo.filename}
              data-testid="viewer-thumbnail"
              draggable={false}
              onLoad={() => {
                // Instant reveal when image is cached (swipe leaves container at opacity 0)
                const el = coffSwipe.photoRef.current;
                if (el && el.style.opacity === '0') {
                  el.style.transition = 'opacity 0.1s ease-out';
                  el.style.opacity = '1';
                }
              }}
              style={{
                width: '100%', height: '100%', objectFit: 'contain',
                transform: `rotate(${rotation}deg)`,
                willChange: 'contents',
              }}
            />
            {!isMobile && (
              <img
                ref={hdRef}
                src={`${API}/photos/${photo.id}/image?token=${token}`}
                alt=""
                data-testid="viewer-hd-image"
                draggable={false}
                onLoad={e => { e.target.style.opacity = '1'; }}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%', objectFit: 'contain',
                  transform: `rotate(${rotation}deg)`,
                  transition: 'opacity 0.4s ease, transform 0.3s ease',
                  opacity: 0,
                }}
              />
            )}
          </div>

          {/* Metadata bar — always visible below the photo */}
          <div data-no-close data-testid="viewer-metadata" style={{
            width: '100%', maxWidth: 500, padding: '8px 16px 80px',
            flexShrink: 0,
          }}>
            {/* Caption input — isolated to prevent gallery re-render on mobile */}
            <IsolatedCaptionInput
              photoId={photo.id}
              initialCaption={photo.caption}
              saveCaption={saveCaption}
              placeholder={t('viewer_add_caption')}
              inputFocusedRef={inputFocusedRef}
            />

            {/* Date info */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 4 }}>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37' }}>
                {t('viewer_sealed_on')} {new Date(photo.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <input
                data-testid="viewer-date-input"
                type="date"
                value={(() => {
                  const ta = photo.taken_at;
                  if (!ta) return '';
                  try { return new Date(ta).toISOString().split('T')[0]; } catch { return ''; }
                })()}
                onChange={async (e) => {
                  e.stopPropagation();
                  const dateVal = e.target.value;
                  if (!dateVal) return;
                  const dt = new Date(dateVal + 'T12:00:00');
                  const iso = dt.toISOString();
                  try {
                    const axios = (await import('axios')).default;
                    await axios.patch(`${API}/photos/${photo.id}`, { taken_at: iso }, { headers: { Authorization: `Bearer ${token}` } });
                    setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, taken_at: iso, year: dt.getFullYear(), month: dt.getMonth() + 1 } : p));
                  } catch { /* silent */ }
                }}
                onFocus={() => { if (inputFocusedRef) inputFocusedRef.current = true; document.body.classList.add('bobine-keyboard-active'); }}
                onBlur={() => { if (inputFocusedRef) inputFocusedRef.current = false; document.body.classList.remove('bobine-keyboard-active'); }}
                onClick={e => e.stopPropagation()}
                style={{
                  fontFamily: "'Georgia', serif", fontSize: 11,
                  color: photo.taken_at ? '#D4AF37' : '#D4AF37',
                  backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.15)',
                  borderRadius: 4, padding: '2px 8px', outline: 'none',
                  cursor: 'pointer', colorScheme: 'dark',
                }}
              />
            </div>

            {/* Audio recorder */}
            <AudioRecorder
              photoId={photo.id}
              existingAudio={photo.audio_filename}
              existingTranscription={photo.audio_transcription}
              token={token}
              onAudioSaved={(pid, filename, transcription) => {
                setPhotos(prev => prev.map(p => p.id === pid ? {
                  ...p,
                  ...(filename !== null ? { audio_filename: filename } : {}),
                  ...(transcription !== undefined ? { audio_transcription: transcription } : {}),
                } : p));
              }}
              onAudioDeleted={(pid) => {
                setPhotos(prev => prev.map(p => p.id === pid ? { ...p, audio_filename: undefined, audio_transcription: undefined } : p));
              }}
            />

            {/* Approved Eclaireur comments */}
            {(() => {
              const approvedComments = pendingComments.filter(c => c.status === 'approved' && c.photo_id === photo.id);
              if (approvedComments.length === 0) return null;
              return (
                <div data-testid="viewer-eclaireur-comments" style={{ marginTop: 12, width: '100%' }}>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8, textAlign: 'center' }}>
                    {t('viewer_comments') || 'Commentaires'}
                  </p>
                  {approvedComments.map(c => (
                    <div key={c.id} data-testid={`viewer-comment-${c.id}`} style={{
                      padding: '10px 14px', marginBottom: 6, borderRadius: 8,
                      backgroundColor: 'rgba(212,175,55,0.04)',
                      border: '1px solid rgba(212,175,55,0.12)',
                    }}>
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', fontWeight: 600 }}>
                        {c.guest_name}
                      </span>
                      {c.has_audio && (
                        <button
                          data-testid={`viewer-play-audio-${c.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const btn = e.currentTarget;
                            if (btn._audio && !btn._audio.paused) {
                              btn._audio.pause(); btn._audio.currentTime = 0; btn._audio = null;
                              btn.querySelector('span').textContent = '\u00c9couter';
                              return;
                            }
                            const a = new Audio(`${API}/eclaireur/audio/${c.id}`);
                            btn._audio = a;
                            btn.querySelector('span').textContent = 'Arr\u00eater';
                            a.play();
                            a.onended = () => { btn._audio = null; btn.querySelector('span').textContent = '\u00c9couter'; };
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 10,
                            fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37',
                            background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)',
                            borderRadius: 14, padding: '3px 12px', cursor: 'pointer',
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="#D4AF37"><polygon points="5 3 19 12 5 21" /></svg>
                          <span>{t('ecl_listen')}</span>
                        </button>
                      )}
                      {c.text && c.text !== '(message vocal)' && (
                        <p style={{
                          fontFamily: "'Georgia', serif", fontSize: 12, color: '#FAF3E0',
                          fontStyle: 'italic', lineHeight: 1.5, marginTop: 4, marginBottom: 0,
                        }}>
                          "{c.text}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Position indicators */}
      <div style={{
        position: 'fixed', bottom: 8, left: '50%',
        transform: 'translateX(-50%)', zIndex: 69,
        display: 'flex', gap: 8, alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)', padding: '4px 12px', borderRadius: 16,
      }}>
        {photos.length <= 12 ? photos.map((_, i) => (
          <button
            key={i}
            data-testid={`viewer-dot-${i}`}
            onClick={e => { e.stopPropagation(); setViewerIndex(i); }}
            style={{
              width: i === viewerIndex ? 10 : 6,
              height: i === viewerIndex ? 10 : 6,
              borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0,
              backgroundColor: i === viewerIndex ? '#D4AF37' : 'rgba(212,175,55,0.3)',
              transition: 'all 0.3s',
              boxShadow: i === viewerIndex ? '0 0 8px rgba(212,175,55,0.5)' : 'none',
            }}
          />
        )) : (
          <span style={{
            fontFamily: "'Georgia', serif", fontSize: 13,
            color: '#D4AF37', letterSpacing: 1,
          }}>
            {viewerIndex + 1} / {photos.length}
          </span>
        )}
      </div>
    </div>
  );
};

export default PhotoViewer;
